import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import client from "@/api/client";
import { CalendarDays, ChevronRight, Newspaper, ExternalLink, RefreshCw } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function OutlookPage() {
  const [outlooks, setOutlooks] = useState([]);
  const [active, setActive] = useState(0);
  const [goldNews, setGoldNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    client.get("/outlooks").then((r) => setOutlooks(r.data.outlooks));
  }, []);

  const loadGoldNews = async () => {
    setNewsLoading(true);
    try {
      const { data } = await client.get("/intel/headlines");
      const gold = data.items.filter((i) => i.category === "gold");
      const commodities = data.items.filter((i) => i.category === "commodities");
      setGoldNews([...gold, ...commodities].slice(0, 30));
    } catch (e) { /* silent */ }
    setNewsLoading(false);
  };
  useEffect(() => { loadGoldNews(); }, []);

  // Auto-refresh the Live Gold Feed every 5 min while the student has the page open
  useEffect(() => {
    const id = setInterval(loadGoldNews, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const o = outlooks[active];

  return (
    <Layout>
      <div data-testid="outlook-page">
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays size={16} className="text-[#D4AF37]" />
          <p className="overline">Weekly Market Outlook</p>
        </div>
        <h1 className="font-display text-5xl mb-2">
          The Week Ahead<span className="text-[#D4AF37]">.</span>
        </h1>
        <p className="text-neutral-500 max-w-2xl mb-10 font-body">
          The Boardroom&apos;s reading of the week ahead: bias, focus pairs, key levels, news events, and pre-mapped setups.
        </p>

        {/* LIVE GOLD FEED — always fresh, primary lens for this week's read */}
        <section className="mb-14 border border-[#1f1f1f] bg-[#0a0a0a]" data-testid="outlook-gold-feed">
          <div className="px-5 py-3 border-b border-[#1f1f1f] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Newspaper size={14} className="text-[#D4AF37]" />
              <p className="overline text-[#D4AF37]">Live Gold Feed</p>
              <span className="text-[9px] uppercase tracking-widest text-neutral-600 ml-2">
                XAU-focused headlines · auto-refresh every 5 min
              </span>
            </div>
            <button onClick={loadGoldNews} disabled={newsLoading}
                    data-testid="gold-feed-refresh"
                    className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#D4AF37] flex items-center gap-1.5">
              <RefreshCw size={11} className={newsLoading ? "animate-spin" : ""} />
              {newsLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
          <div className="p-5 max-h-[520px] overflow-y-auto">
            {!newsLoading && goldNews.length === 0 && (
              <p className="text-neutral-500 text-sm">No gold headlines available right now.</p>
            )}
            <ol className="space-y-3">
              {goldNews.map((h, i) => (
                <li key={i} className="border-l-2 border-[#1f1f1f] hover:border-[#D4AF37] pl-4 py-1 transition group"
                    data-testid={`outlook-gold-item-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] uppercase tracking-widest ${
                      h.category === "gold" ? "text-[#D4AF37]" : "text-[#AA8A25]"
                    }`}>◇ {h.category}</span>
                    <span className="text-[10px] text-neutral-600">{h.source}</span>
                    {h.published_at && (
                      <span className="text-[10px] text-neutral-600 ml-auto">{timeAgo(h.published_at)}</span>
                    )}
                  </div>
                  <a href={h.link} target="_blank" rel="noopener noreferrer"
                     className="font-body text-neutral-200 group-hover:text-white text-[14px] leading-snug inline-flex items-start gap-1.5">
                    {h.title}
                    <ExternalLink size={11} className="text-neutral-600 group-hover:text-[#D4AF37] mt-1 shrink-0" />
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {outlooks.length === 0 && (
          <div className="border border-dashed border-[#1f1f1f] p-16 text-center text-neutral-600 text-sm">
            No outlook published yet. Check back on Sunday.
          </div>
        )}

        {outlooks.length > 0 && (
          <div className="grid lg:grid-cols-[240px_1fr] gap-10">
            <aside className="lg:sticky lg:top-24 self-start">
              <p className="overline mb-4">Archive</p>
              <ol className="space-y-2">
                {outlooks.map((x, i) => (
                  <li key={x.id}>
                    <button data-testid={`outlook-item-${i}`}
                      onClick={() => setActive(i)}
                      className={`text-left w-full text-sm py-2 border-l-2 pl-4 transition ${
                        i === active ? "border-[#D4AF37] text-white" : "border-[#222] text-neutral-500 hover:text-neutral-200 hover:border-neutral-500"
                      }`}>
                      <span className="block font-mono text-[10px] text-neutral-500">Week of {x.week_of}</span>
                      {x.title}
                    </button>
                  </li>
                ))}
              </ol>
            </aside>

            {o && (
              <article className="max-w-3xl">
                <p className="overline">Week of {o.week_of}</p>
                <h2 className="font-display text-4xl md:text-5xl mt-2 mb-6 leading-tight">{o.title}</h2>
                <div className="gold-divider mb-8" />

                {o.bias && <Section label="Bias" body={o.bias} testId="outlook-bias" />}
                {o.focus_pairs && <Section label="Focus Pairs / Instruments" body={o.focus_pairs} testId="outlook-focus" />}
                {o.key_levels && <Section label="Key Levels" body={o.key_levels} testId="outlook-levels" />}
                {o.news_events && <Section label="High-Impact News" body={o.news_events} testId="outlook-news" />}
                {o.setups && <Section label="Pre-Mapped Setups" body={o.setups} testId="outlook-setups" />}
                {o.notes && <Section label="Additional Notes" body={o.notes} testId="outlook-notes" />}

                <p className="text-[10px] text-neutral-600 mt-10 uppercase tracking-widest">
                  Published {new Date(o.at).toLocaleString()} · {o.author}
                </p>
              </article>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Section({ label, body, testId }) {
  return (
    <div className="mb-8" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <ChevronRight size={14} className="text-[#D4AF37]" />
        <p className="overline">{label}</p>
      </div>
      <div className="text-neutral-200 text-[16px] leading-[1.85] font-body whitespace-pre-line">
        {body}
      </div>
    </div>
  );
}
